import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ComponentProps, PropsWithChildren, ReactNode } from 'react'
import { cn } from '~/shared/lib/cn'

function TooltipProvider({
  delayDuration = 600,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function TooltipRoot({ ...props }: ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        {...props}
      />
    </TooltipProvider>
  )
}

function TooltipTrigger({ ...props }: ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
    />
  )
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-surface-high-inverse text-content-highest-emphasis-inverse animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-[200px] origin-(--radix-tooltip-content-transform-origin) rounded-sm px-2 py-1 text-xs leading-4 font-medium tracking-[0.4px]',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          asChild
          className="z-50"
          width={12}
          height={6}
        >
          <div className="border-t-surface-high-inverse h-0 w-0 origin-center border-x-[6px] border-t-[6px] border-x-transparent" />
        </TooltipPrimitive.Arrow>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

interface TooltipProps extends PropsWithChildren {
  content: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side']
  delayDuration?: number
  align?: ComponentProps<typeof TooltipPrimitive.Content>['align']
}

export function Tooltip({
  children,
  content,
  open,
  onOpenChange,
  side = 'top',
  sideOffset = 0,
  align = 'center',
  disableHoverableContent = false,
  delayDuration,
}: TooltipProps & { sideOffset?: number; disableHoverableContent?: boolean }) {
  return (
    <TooltipRoot
      open={open}
      onOpenChange={onOpenChange}
      disableHoverableContent={disableHoverableContent}
      delayDuration={delayDuration}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        sideOffset={sideOffset}
        align={align}
      >
        {content}
      </TooltipContent>
    </TooltipRoot>
  )
}
